import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import VinInput from '../../Car/NewCar/VinInput';
import findByVin from '../../../api/cars/findByVin';
import SelectedCarFields from './SelectedCarFields';
import { FULL_VIN_LENGTH, colorName, isSameVin, vinSearchQuery } from '../../Car/NewCar/vinSearch';
import ConsignmentCreate from '../../Car/NewCar/Consignment/ConsignmentCreate';
import { needsNewCar } from './needsNewCar';
import formatNumber from '../../../utils/formatNumber';
import formStyles from '../../../components/Form/form.module.css';
import { textStyles } from '../../../constants/textStyles';

const card = {
  display: 'flex',
  gap: 16,
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 16px',
  border: '1px solid #E3E3E3',
  borderRadius: 10,
  background: '#fff',
};
const link = {
  border: 'none',
  background: 'none',
  padding: 0,
  color: 'var(--brand-primary)',
  textDecoration: 'underline',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const toVin = (value) =>
  String(value || '')
    .replace(/[^0-9a-z]/gi, '')
    .toUpperCase();

/**
 * Автомобиль на первом шаге сделки у серого дилера: вместо окна с каталогом — поле VIN.
 *  · вводим номер — под полем список похожих машин из базы, «Выбрать» ставит машину в сделку;
 *  · такого VIN нет — тут же открывается форма нового автомобиля; сохраняется вместе со сделкой
 *    (экран сделки зовёт ref.submitNewCar()).
 */
const GreyDealCar = forwardRef(({ cars, onPick, onRemove, error }, ref) => {
  const [vin, setVin] = useState('');
  const [search, setSearch] = useState(null);
  const newCarRef = useRef(null);

  const selected = cars?.[0];
  const showNewForm = !selected && needsNewCar(vin, search);

  // «Выбрать» срабатывает сразу: карточка машины появляется из данных подсказки, пока полная карточка грузится.
  const [pending, setPending] = useState(null);
  // Данные выбранной машины свёрнуты: менеджеру обычно хватает карточки, остальное открывается стрелкой.
  const [expanded, setExpanded] = useState(false);
  const shown = selected || pending;
  const pickFound = async (found) => {
    setPending(found);
    try {
      await onPick(found);
    } finally {
      setPending(null);
    }
  };

  // Возвращает созданное авто; null — формы нет или в ней что-то не заполнено / не сохранилось.
  useImperativeHandle(ref, () => ({
    hasNewCar: () => showNewForm,
    // Ввели полный VIN, но не нажали «Выбрать», и идут дальше: если такая машина есть, берём её сами.
    // Спрашиваем бэк напрямую — подсказка могла ещё не успеть ответить.
    findExisting: async () => {
      if (selected || String(vin).length !== FULL_VIN_LENGTH) return null;
      const res = await findByVin(vinSearchQuery(vin)).catch(() => null);
      const list = Array.isArray(res?.data) ? res.data : [];
      return list.find((car) => isSameVin(car, vin)) || null;
    },
    submitNewCar: () => (newCarRef.current ? newCarRef.current.submit() : Promise.resolve(null)),
  }));

  return (
    <>
      <div className={formStyles.wrapper} style={{ marginTop: 0 }}>
        <p style={textStyles.formTitle}>Автомобиль</p>

        {shown ? (
          <div style={{ ...card, marginTop: 16, opacity: selected ? 1 : 0.65 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {[shown.mark, shown.model].filter(Boolean).join(' ')}
                {shown.issueYear ? `, ${shown.issueYear}` : ''}
              </div>
              <div style={{ fontSize: 13, color: '#8B8B8B', marginTop: 4 }}>
                {[shown.vin, colorName(shown.color), shown.price ? `${formatNumber(shown.price)} ₸` : '']
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            {selected ? (
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <button
                  type="button"
                  style={{ ...link, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                  aria-expanded={expanded}
                  onClick={() => setExpanded((value) => !value)}
                >
                  {expanded ? 'Свернуть' : 'Все данные'}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    aria-hidden="true"
                    style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                  >
                    <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  style={link}
                  onClick={() => {
                    setVin('');
                    setSearch(null);
                    setExpanded(false);
                    onRemove(selected);
                  }}
                >
                  Заменить
                </button>
              </div>
            ) : (
              <span style={{ fontSize: 13, color: '#8B8B8B', whiteSpace: 'nowrap' }}>Загружаем…</span>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 16, maxWidth: 520 }}>
            <VinInput
              label="VIN автомобиля"
              placeholder="Введите VIN — найдём в базе или добавим новый"
              name="dealVin"
              maxLength={17}
                required
              value={vin}
              transform={toVin}
              onChange={(next) => setVin(typeof next === 'string' ? next : toVin(next?.target?.value))}
              onSearch={setSearch}
              onPickCar={pickFound}
              error={Boolean(error)}
            />
            {error && <p style={{ margin: '6px 0 0', fontSize: 12, color: '#EB5757' }}>{error}</p>}
          </div>
        )}

        {showNewForm && (
          <p style={{ margin: '16px 0 0', fontSize: 14, color: '#6A6A6A' }}>
            Такого VIN в базе нет. Заполните данные ниже — автомобиль добавится вместе со сделкой.
          </p>
        )}
      </div>

      {selected && expanded && <SelectedCarFields car={selected} />}

      {showNewForm && <ConsignmentCreate ref={newCarRef} embedded vin={vin} />}
    </>
  );
});

export default GreyDealCar;
